import React, { useEffect, useState, useContext } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { DashboardContext } from "../../../../../contexts/Dashboard/DashboardContext";
import HeaderComponent from "../../../../HeaderComponent";
import LoadingGraphics from "../../../../Loading/LoadingGraphics";
import { Routes } from ".";

interface TMA {
  region?: string,
  tma_today: number,
  tma_last_4_weekdays: number,
  tma_last_28_days: number
}

export const ChartBar = ({ data }: Routes) => {
  const { getSizesComponent, setFontSize } = useContext(DashboardContext);
  const [completeRequisition, setCompleteRequisition] = useState(false);
  const [heightChart, setHeightChart] = useState(300);
  const [widthChart, setWidthChart] = useState(300);

  const [tmaToday, setTmaToday] = useState<number[]>([]);
  const [tmaLast4Weekdays, setTmaLast4Weekdays] = useState<number[]>([]);
  const [tmaLast28Days, setTmaLast28Days] = useState<number[]>([]);
  const [max, setMax] = useState<number[]>([]);
  const [dayOfWeek, setDayOfWeek] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    const today= new Date();
    const day = today.getDay();
    const weekday = today.toLocaleString('pt-br', {  weekday: 'long' }).split('-');
    if(day === 0 || day === 6) {
      setDayOfWeek(`${weekday[0]}s`);
    } else {
      setDayOfWeek(`${weekday[0]}s-${weekday[1]}s`);
    }
    // Adicionando 50 nos valores das barras para melhorar a visualização
    // das barras com valores pequenos
    // Esses valores são corrigidos no datalabels, pra não influenciar os dados
    setTmaToday(
      data.tma.map(function (route: TMA) {
        if(Number(route.tma_today === 0))
          return route.tma_today
        else
          return route.tma_today + 50;
      })
    );
    setTmaLast4Weekdays(
      data.tma.map(function (route: TMA) {
        if(Number(route.tma_last_4_weekdays === 0))
          return route.tma_last_4_weekdays
        else
          return route.tma_last_4_weekdays + 50;
      })
    );
    setTmaLast28Days(
      data.tma.map(function (route: TMA) {
        if(Number(route.tma_last_28_days === 0))
          return route.tma_last_28_days
        else
          return route.tma_last_28_days + 50;
      })
    );
    setCategories(
      data.tma.map(function (route: TMA) {
        return route.region ? route.region : '';
      })
    );
    setMax(
      data.tma.map(function (route: TMA) {
        return Number(route.tma_today) + Number(route.tma_last_4_weekdays) + Number(route.tma_last_28_days);
      })
    );

  }, [data]);

  const series = [
    {
      name: "Tempo médio histórico (4 últimas semanas)",
      group: 'tma',
      data: tmaLast28Days,
    },
    {
      name: `Tempo médio histórico (${dayOfWeek})`,
      group: 'tma',
      data: tmaLast4Weekdays,
    },
    {
      name: "Tempo médio atual",
      group: 'tma',
      data: tmaToday,
    },
  ];

  const options: ApexOptions = {
    chart: {
      animations: {
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
        enabled: false,
      },
      type: "bar",
      stacked: true,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: true,
      }
    },
    colors: ["#819E43", "#D1423F", "#3D699C"],
    stroke: {
      show: true,
      width: 2,
      colors: ["#819E43", "#D1423F", "#3D699C"],
    },
    responsive: [
      {
        breakpoint: 480,
      },
    ],
    dataLabels: {
      enabled: true,
      textAnchor: "middle",
      style: {
        fontSize: "13px",
        fontWeight: 900,
      },
      formatter: function (val, config) {
        if(config.seriesIndex === 0) {
          const hours = Math.floor((config.globals.seriesLog[0][config.dataPointIndex] - 50) / 60);
          const minutes = Math.floor((config.globals.seriesLog[0][config.dataPointIndex] - 50) % 60);
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        else if(config.seriesIndex === 1) {
          const hours = Math.floor((config.globals.seriesLog[1][config.dataPointIndex] - 50) / 60);
          const minutes = Math.floor((config.globals.seriesLog[1][config.dataPointIndex] - 50) % 60);
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }          
        else if(config.seriesIndex === 2) {
          const hours = Math.floor((config.globals.seriesLog[2][config.dataPointIndex] - 50) / 60);
          const minutes = Math.floor((config.globals.seriesLog[2][config.dataPointIndex] - 50) % 60);
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        else
          return `${val}` ;
      },
    },
    plotOptions: {
      bar: {
        barHeight: "100%",
        horizontal: false,
      },
    },
    xaxis: {
      categories: categories,
      type: 'category',
      labels: {
        hideOverlappingLabels: false,
        trim: false,
        rotate: -60
      },
    },
    yaxis: {
      min: 0,
      max: Math.max(...max),
      show: false,
    },
    grid: {
      padding: {
        bottom: 120, // Padding from the bottom of the chart
        left: 100    // Padding from the left of the chart
      },
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    legend: { 
      show: true,
      position: 'top',
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      enabledOnSeries: [0,1,2],
      x: {
        formatter: function(value, { seriesIndex }) {
          if(seriesIndex === 0) {
            return `${value} </br> Tempo médio histórico (últimas 4 semanas)`           
          } else if(seriesIndex === 1) {
            return `${value} </br> Tempo médio histórico (${dayOfWeek})`
          } else if(seriesIndex === 2) {
            return `${value} </br> Tempo médio atual`
          } else {
            return `${value}`;
          }
        }
      },
      y: {
        formatter: function (value) {
          return `${value - 50} minutos`
        },
        title: {
          formatter: function(seriesName) {
            return `${seriesName}`
          }
        }
      }
    }
  };

  useEffect(() => {
    const sizes = getSizesComponent({ id: "chart-routes-average-stay-on-interval" });
    if (sizes.width < 345) {
      setFontSize({ id: "chart-routes-average-stay-on-interval", width: sizes.width });
    }

    setHeightChart(sizes.height - 40);
    setWidthChart(sizes.width);
    setCompleteRequisition(true);
  }, []);

  return completeRequisition ? (
    <div className="h100">
      <HeaderComponent
        title={`Gráfico - Tempo Médio de Entregas em Minutos`}
        id="chart-routes-average-stay-on-interval"
      />
      <div id="chart">
        <Chart
          type="bar"
          options={options}
          series={series}
          height={heightChart}
          width={widthChart}
        />
      </div>
    </div>
  ) : (
    <LoadingGraphics/>
  );
};